import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Error handling utilities
export interface ApiError {
  code: string;
  expected?: string;
  received?: string;
  path: string[];
  message: string;
}

export interface ApiResponse {
  success: boolean;
  data?: any;
  message?: string;
  errorCode?: string;
  timestamp?: string;
}

export function extractErrorMessage(response: ApiResponse): string {
  // If it's a successful response, return empty string
  if (response.success) {
    return '';
  }

  // Handle validation errors with detailed error array
  if (response.data?.errors && Array.isArray(response.data.errors)) {
    const errors = response.data.errors as ApiError[];
    
    // If multiple errors, show them as a list
    if (errors.length > 1) {
      return errors.map(error => {
        const field = error.path.join('.');
        return `${field}: ${error.message}`;
      }).join('\n');
    }
    
    // Single error
    if (errors.length === 1) {
      const error = errors[0];
      const field = error.path.join('.');
      return field ? `${field}: ${error.message}` : error.message;
    }
  }

  // Fallback to message or generic error
  return response.message || 'An unexpected error occurred';
}

export function formatValidationErrors(errors: ApiError[]): { [field: string]: string } {
  const formattedErrors: { [field: string]: string } = {};
  
  errors.forEach(error => {
    const field = error.path.join('.');
    if (field) {
      formattedErrors[field] = error.message;
    }
  });
  
  return formattedErrors;
}

export function isValidationError(response: ApiResponse): boolean {
  return response.errorCode === 'VALIDATION_ERROR' && 
         response.data?.errors && 
         Array.isArray(response.data.errors);
}
